import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import ResponseError from "#/types/responseError"
import { useFlags } from "launchdarkly-react-client-sdk"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"

const SPRINTS_CANDIDACY_QUERY_KEY = "sprintsCandidacyQueryKey"

export type SprintsCandidacyResponse = {
  memberIsSprintsCandidate: boolean
  partnerIsSprintsCandidate: boolean
}

export function useSprintsCandidacyQuery<TData = SprintsCandidacyResponse>(
  options?: Pick<UseQueryOptions<SprintsCandidacyResponse, ResponseError, TData>, "select" | "enabled">
) {
  return useQuery<SprintsCandidacyResponse, ResponseError, TData>({
    queryKey: [SPRINTS_CANDIDACY_QUERY_KEY],
    queryFn: () => carrotClient.getSprintsCandidacy(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 0,
    cacheTime: 0,
    retry: buildCarrotApiRetryPolicy(1),
    ...(options || {})
  })
}

export type SprintsAuthorizationResponse = {
  memberIsSprintsAuthorized: boolean
  partnerIsSprintsAuthorized: boolean
}

const SPRINTS_AUTHORIZATION_QUERY_KEY = "sprintsAuthorizationQueryKey"
export function useSprintsAuthorizationQuery<TData = SprintsAuthorizationResponse>(
  options?: Pick<UseQueryOptions<SprintsAuthorizationResponse, ResponseError, TData>, "select" | "enabled">
) {
  return useQuery<SprintsAuthorizationResponse, ResponseError, TData>({
    queryKey: [SPRINTS_AUTHORIZATION_QUERY_KEY],
    queryFn: () => carrotClient.getSprintsAuthorization(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 0,
    cacheTime: 0,
    retry: buildCarrotApiRetryPolicy(1),
    ...(options || {})
  })
}

export function useIsSprintsVisible() {
  const { sprintsJourneyExpansion } = useFlags()
  const { data: isSprintsEnabledByBenefit, isLoading: isBenefitLoading } = useBenefit({
    select: (benefit) => benefit.sprints?.fertilityCare?.enabled
  })
  const { data: isSprintsAuthorized, isLoading: isSprintsAuthLoading } = useSprintsAuthorizationQuery({
    select: (data) => data.memberIsSprintsAuthorized || data.partnerIsSprintsAuthorized
  })

  const isVisible = sprintsJourneyExpansion ? isSprintsEnabledByBenefit : isSprintsAuthorized
  const isLoading = sprintsJourneyExpansion ? isBenefitLoading : isSprintsAuthLoading

  return { isVisible, isLoading }
}
